<script setup lang="ts">
import {computed} from "vue";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";
import {FrequentationHour, FrequentationWeek} from "@/core/interfaces/Frequentation";
import {colorForLevel, describeSlot} from "@/agenda/lib/frequentation";
import {parseAgendaDate} from "@/agenda/lib/agendaTime";

/**
 * The expected load of a week as a table: days across, hours down — the
 * reading of a calendar, with more hours than days so the long axis goes
 * down. Days the pharmacy is closed do not appear; hours nobody saw open on
 * a given day are left blank.
 */
const props = defineProps<{
    week: FrequentationWeek;
}>();

/** Every hour between the earliest and the latest the week ever saw open. */
const hours = computed(() => {
    const seen = props.week.days.flatMap((day) => day.hours.map((slot) => slot.hour));

    if (!seen.length) {
        return [];
    }

    const first = Math.min(...seen);
    const last = Math.max(...seen);

    return Array.from({length: last - first + 1}, (_, index) => first + index);
});

interface Cell {
    hour: number;
    color: string;
    description: {title: string; lines: string[]};
}

/**
 * One column per day, one cell per displayed hour (null when the pharmacy
 * was closed), each cell described once here rather than at every render.
 */
const days = computed(() => props.week.days.map((day) => {
    const slots = new Map<number, FrequentationHour>(day.hours.map((slot) => [slot.hour, slot]));

    return {
        date: day.date,
        label: parseAgendaDate(day.date).format('dddd D'),
        cells: hours.value.map((hour): Cell | null => {
            const slot = slots.get(hour);

            return slot
                ? {hour, color: colorForLevel(slot.level), description: describeSlot(slot, props.week.average_minutes)}
                : null;
        })
    };
}));

const columns = computed(() => `56px repeat(${days.value.length}, minmax(0, 1fr))`);
</script>

<template>
  <TooltipProvider>
    <div class="grid gap-1 text-sm" :style="{gridTemplateColumns: columns}">
      <div />
      <p
        v-for="day in days"
        :key="day.date"
        class="truncate pb-1 text-center text-xs font-medium text-gray-600 first-letter:uppercase"
      >
        {{ day.label }}
      </p>
      <template v-for="(hour, row) in hours" :key="hour">
        <p class="pr-2 text-right text-xs leading-9 text-gray-500 tabular-nums">
          {{ hour }} h
        </p>
        <template v-for="day in days" :key="`${day.date}-${hour}`">
          <Tooltip v-if="day.cells[row]" :delay-duration="150">
            <TooltipTrigger as-child>
              <div
                class="h-9 rounded-md transition-opacity hover:opacity-80"
                :style="{backgroundColor: day.cells[row]!.color}"
                role="img"
                :aria-label="day.cells[row]!.description.title"
              />
            </TooltipTrigger>
            <TooltipContent side="top" class="max-w-xs text-xs">
              <p class="font-semibold">
                {{ day.cells[row]!.description.title }}
              </p>
              <p v-for="line in day.cells[row]!.description.lines" :key="line">
                {{ line }}
              </p>
            </TooltipContent>
          </Tooltip>
          <div v-else class="h-9 rounded-md bg-gray-50" />
        </template>
      </template>
    </div>
  </TooltipProvider>
</template>
